import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { whatsappGroupsApi, type WhatsappOperationAnalysis } from '../api/whatsapp-groups.api';
import { toast } from '@/shared/lib/toast';

export function useWhatsappGroups(filters?: {
  status?: string;
  clientId?: string;
  search?: string;
  periodDays?: number;
}) {
  return useQuery({
    queryKey: ['whatsapp-groups', filters],
    queryFn: async () => {
      try {
        const response = await whatsappGroupsApi.getGroups(filters);
        const groups = response.data.data || response.data || [];
        return Array.isArray(groups) ? groups : [];
      } catch (error: unknown) {
        console.error('Error fetching groups:', error);
        const message = error && typeof error === 'object' && 'response' in error
          ? (error.response as { data?: { message?: string } })?.data?.message
          : undefined;
        toast.error(message || 'Erro ao buscar grupos do WhatsApp');
        return [];
      }
    },
  });
}

export function useWhatsappGroup(groupJid: string) {
  return useQuery({
    queryKey: ['whatsapp-group', groupJid],
    queryFn: async () => {
      try {
        const response = await whatsappGroupsApi.getGroup(groupJid);
        return response.data.data || response.data || null;
      } catch (error: unknown) {
        console.error('Error fetching group:', error);
        const message = error && typeof error === 'object' && 'response' in error
          ? (error.response as { data?: { message?: string } })?.data?.message
          : undefined;
        toast.error(message || 'Erro ao buscar detalhes do grupo');
        return null;
      }
    },
    enabled: !!groupJid,
  });
}

export function useWhatsappGroupMessages(
  groupJid: string,
  filters?: {
    limit?: number;
    offset?: number;
  }
) {
  return useQuery({
    queryKey: ['whatsapp-group-messages', groupJid, filters],
    queryFn: async () => {
      try {
        const response = await whatsappGroupsApi.getGroupMessages(groupJid, filters);
        const messages = response.data.data || response.data || [];
        return Array.isArray(messages) ? messages : [];
      } catch (error: unknown) {
        console.error('Error fetching messages:', error);
        return [];
      }
    },
    enabled: !!groupJid,
  });
}

export function useLastOperationAnalysis() {
  return useQuery({
    queryKey: ['whatsapp-last-analysis'],
    queryFn: async () => {
      try {
        const response = await whatsappGroupsApi.getLastOperationAnalysis();
        return response.data.data || response.data || null;
      } catch (error: unknown) {
        console.error('Error fetching last analysis:', error);
        return null;
      }
    },
  });
}

export function useOperationAnalysisHistory(limit = 30) {
  return useQuery({
    queryKey: ['whatsapp-analysis-history', limit],
    queryFn: async () => {
      try {
        const response = await whatsappGroupsApi.getOperationAnalysisHistory(limit);
        const analyses = response.data.data || response.data || [];
        return Array.isArray(analyses) ? analyses : [];
      } catch (error: unknown) {
        console.error('Error fetching analysis history:', error);
        return [];
      }
    },
  });
}

export function useTriggerOperationAnalysis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: whatsappGroupsApi.triggerOperationAnalysis,
    onSuccess: () => {
      toast.success('🤖 Análise IA iniciada! Aguarde alguns segundos...');

      // Fazer polling para verificar quando a análise terminar
      let pollCount = 0;
      const maxPolls = 20; // Máximo 40 segundos (20 * 2s)

      const pollInterval = setInterval(async () => {
        pollCount++;

        // Refetch a última análise
        const result = await queryClient.fetchQuery<WhatsappOperationAnalysis | null>({
          queryKey: ['whatsapp-last-analysis'],
        });

        // Se encontrou uma análise muito recente (últimos 10 segundos), assumimos que é a nova
        if (result && new Date(result.createdAt).getTime() > Date.now() - 10000) {
          clearInterval(pollInterval);
          toast.success('✅ Análise concluída com sucesso!');
          queryClient.invalidateQueries({ queryKey: ['whatsapp-last-analysis'] });
          queryClient.invalidateQueries({ queryKey: ['whatsapp-analysis-history'] });
          queryClient.invalidateQueries({ queryKey: ['whatsapp-groups'] });
        }

        // Parar após timeout
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          toast.info('⏱️ Análise em andamento. Atualize a página em instantes.');
        }
      }, 2000); // Verificar a cada 2 segundos
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`❌ Erro ao iniciar análise: ${message}`);
    },
  });
}

export function useSyncGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: whatsappGroupsApi.syncGroups,
    onSuccess: () => {
      toast.success('Sincronização iniciada com sucesso');
      queryClient.invalidateQueries({ queryKey: ['whatsapp-groups'] });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao sincronizar grupos: ${message}`);
    },
  });
}

export function useLinkClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupJid, clientId }: { groupJid: string; clientId: string }) =>
      whatsappGroupsApi.linkClient(groupJid, clientId),
    onSuccess: () => {
      toast.success('Cliente vinculado com sucesso');
      queryClient.invalidateQueries({ queryKey: ['whatsapp-group'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-groups'] });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao vincular cliente: ${message}`);
    },
  });
}
